/**
 * 玩家回放中心 - 列表页 (Phase 2)
 *
 * 数据源: /api/v1/admin/playback/sessions
 * 受众: 仅管理员/GM（后端 require_admin 守卫）
 *
 * 商户筛选器: 接通 /merchant/merchants 真实列表 (替代之前硬编码的 "默认商户 (1)").
 * 保留该筛选器因为 admin/GM 在多租户场景下需要按商户隔离查看会话.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Table,
  Tag,
  Space,
  Input,
  Select,
  Button,
  message,
  Tooltip,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ReloadOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import { merchantApi, playbackApi } from '@/services/api'
import type { PlaybackSessionSummary } from '@/types/playback'

const { Search } = Input

interface FilterState {
  world_id?: string
  player_id?: string
  merchant_id?: string
}

interface MerchantOption {
  value: string
  label: string
}

/** 状态色映射（世界状态） */
const STATUS_COLOR: Record<string, string> = {
  running: 'processing',
  ended: 'default',
  settling: 'warning',
  settled: 'success',
  failed: 'error',
}

/** 结局类型色映射 */
const ENDING_COLOR: Record<string, string> = {
  perfect: 'gold',
  good: 'green',
  normal: 'blue',
  bad: 'orange',
  tragic: 'red',
}

const PlaybackList: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<PlaybackSessionSummary[]>([])
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 })
  const [filters, setFilters] = useState<FilterState>({})

  // 商户选项: 动态加载, 失败时降级到当前已选值 (不阻塞主查询)
  const [merchantOptions, setMerchantOptions] = useState<MerchantOption[]>([])
  const [merchantLoading, setMerchantLoading] = useState(false)

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    try {
      const response = await playbackApi.listSessions({
        page: pagination.current,
        page_size: pagination.pageSize,
        world_id: filters.world_id,
        player_id: filters.player_id,
        merchant_id: filters.merchant_id,
      })
      setSessions(response.data?.items ?? [])
      setTotal(response.data?.total ?? 0)
    } catch (error: any) {
      message.error(error?.message ?? '获取回放会话失败')
      console.error('fetchSessions error:', error)
    } finally {
      setLoading(false)
    }
  }, [pagination.current, pagination.pageSize, filters])

  // 加载商户列表 (拉 100 条全量, 失败时降级到空数组)
  const fetchMerchants = useCallback(async () => {
    setMerchantLoading(true)
    try {
      const response = await merchantApi.getMerchants({
        page: 1,
        page_size: 100,
      })
      const items = response.data?.items ?? response.data ?? []
      const options: MerchantOption[] = (items as any[])
        .map((m) => ({
          value: String(m.id),
          label: m.company_name ? `${m.company_name} (${m.id})` : `商户 ${m.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setMerchantOptions(options)
    } catch (error: any) {
      // 不弹错误提示, 静默降级 — 商户列表只是辅助筛选
      console.warn('[PlaybackList] Failed to load merchants:', error?.message ?? error)
      setMerchantOptions([])
    } finally {
      setMerchantLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  useEffect(() => {
    fetchMerchants()
  }, [fetchMerchants])

  const handleWorldSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, world_id: value || undefined }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handlePlayerSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, player_id: value || undefined }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleMerchantChange = (value: string | undefined) => {
    setFilters((prev) => ({ ...prev, merchant_id: value || undefined }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({ current: page, pageSize })
  }

  const handleViewDetail = (record: PlaybackSessionSummary) => {
    navigate(`/playback/${encodeURIComponent(record.world_id)}/${encodeURIComponent(record.player_id)}`)
  }

  const columns = useMemo<ColumnsType<PlaybackSessionSummary>>(
    () => [
      {
        title: 'World ID',
        dataIndex: 'world_id',
        key: 'world_id',
        width: 220,
        ellipsis: true,
        render: (val: string) => (
          <Tooltip title={val}>
            <code className="text-xs">{val}</code>
          </Tooltip>
        ),
      },
      {
        title: '玩家',
        dataIndex: 'player_id',
        key: 'player_id',
        width: 140,
        render: (val: string, record) => (
          <Space direction="vertical" size={0}>
            <span>{record.player_name || val}</span>
            {record.player_name && (
              <span className="text-xs text-gray-400">{val}</span>
            )}
          </Space>
        ),
      },
      {
        title: '角色',
        dataIndex: 'character_name',
        key: 'character_name',
        width: 140,
        render: (val: string | null) => val ?? <span className="text-gray-400">-</span>,
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (val: string) => (
          <Tag color={STATUS_COLOR[val] ?? 'default'}>{val}</Tag>
        ),
      },
      {
        title: '结局',
        key: 'ending',
        width: 160,
        render: (_, record) => {
          if (!record.ending_type) return <span className="text-gray-400">-</span>
          return (
            <Tag color={ENDING_COLOR[record.ending_type] ?? 'default'}>
              {record.ending_name || record.ending_type}
            </Tag>
          )
        },
      },
      {
        title: '得分/排名',
        key: 'score_rank',
        width: 110,
        render: (_, record) => {
          if (record.score == null) return <span className="text-gray-400">-</span>
          return (
            <span>
              {record.score}
              {record.rank != null && (
                <span className="text-gray-400 text-xs"> / #{record.rank}</span>
              )}
            </span>
          )
        },
      },
      {
        title: '开始时间',
        dataIndex: 'started_at',
        key: 'started_at',
        width: 170,
        render: (val: string) => dayjs(val).format('YYYY-MM-DD HH:mm'),
      },
      {
        title: '结束时间',
        dataIndex: 'ended_at',
        key: 'ended_at',
        width: 170,
        render: (val: string | null) =>
          val ? dayjs(val).format('YYYY-MM-DD HH:mm') : <span className="text-gray-400">未结束</span>,
      },
      {
        title: '操作',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看回放
          </Button>
        ),
      },
    ],
    [navigate],
  )

  return (
    <div className="p-6">
      <Card
        title={
          <Space>
            <PlayCircleOutlined />
            <span>回放中心</span>
            <Tag color="blue">{total}</Tag>
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchSessions} loading={loading}>
            刷新
          </Button>
        }
      >
        <Space wrap className="mb-4">
          <Search
            placeholder="按 World ID 过滤"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleWorldSearch}
            style={{ width: 280 }}
          />
          <Search
            placeholder="按 Player ID 过滤"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handlePlayerSearch}
            style={{ width: 240 }}
          />
          <Select
            placeholder="商户"
            allowClear
            loading={merchantLoading}
            onChange={handleMerchantChange}
            style={{ width: 200 }}
            options={merchantOptions}
            notFoundContent={merchantLoading ? '加载中…' : '暂无商户'}
          />
        </Space>

        <Table<PlaybackSessionSummary>
          rowKey={(record) => `${record.world_id}__${record.player_id}`}
          columns={columns}
          dataSource={sessions}
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: handleTableChange,
          }}
          locale={{ emptyText: '暂无回放会话。ai-engine 是否已通过 MQTT 同步数据？' }}
        />
      </Card>
    </div>
  )
}

export default PlaybackList